import {
  cleanDesignAttribute,
  reconcileHighLowLength,
} from "@/lib/designPresentation";
import type { CreateFormData } from "@/components/create/types";
import type { DesignBrief } from "@/types/createAgent";
import type {
  DesignReferenceRole,
  DesignSpecFieldProvenance,
  DesignSpecReference,
  DesignSpec,
} from "@/types/designSpec";
import { designSpecSchema } from "@/types/designSpec";
import { cleanFrontPromptCandidate } from "@/lib/agent/frontPromptText";
import {
  ACCENT_COLOR_CONTEXT,
  DESIGN_COLOR_PATTERN,
  PRIMARY_COLOR_CONTEXT,
} from "@/lib/agent/colorProminence";
import {
  isInDesignAvoidanceScope,
  isWorkflowAvoidRequirement,
  stripAvoidRequirementClauses,
} from "@/lib/agent/designDescription";

function compact(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function values(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(values);
  const text = compact(value);
  if (!text) return [];
  return text
    .split(/\s*(?:,|\/|\band\b|\+)\s*/i)
    .map(item => item.trim())
    .filter(Boolean);
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function unique(items: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of items) {
    const trimmed = compact(item);
    if (!trimmed) continue;
    const key = normalize(trimmed);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

function sameValue(left: string, right: string): boolean {
  return normalize(left) === normalize(right);
}

type VisualScalarField = "length" | "sleeve" | "neckline" | "silhouette";

interface VisualFamilyPattern {
  family: string;
  structured: RegExp;
  prompt: RegExp;
}

const VISUAL_FAMILY_PATTERNS: Record<VisualScalarField, VisualFamilyPattern[]> =
  {
    sleeve: [
      {
        family: "none",
        structured: /\b(?:sleeveless|strapless|no[-\s]?sleeves?)\b/i,
        prompt: /\b(?:sleeveless|strapless|no[-\s]?sleeves?)\b/i,
      },
      {
        family: "bishop",
        structured: /\bbishop\b/i,
        prompt: /\bbishop(?:[-\s]?sleeved|(?:\s+\w+){0,1}\s+sleeves?)\b/i,
      },
      {
        family: "puff",
        structured: /\bpuff(?:ed)?\b/i,
        prompt: /\bpuff(?:ed)?(?:[-\s]?sleeved|(?:\s+\w+){0,1}\s+sleeves?)\b/i,
      },
      {
        family: "cap",
        structured: /\bcap\b/i,
        prompt: /\bcap\s+sleeves?\b/i,
      },
      {
        family: "short",
        structured: /\bshort\b/i,
        prompt: /\bshort(?:\s+\w+){0,1}\s+sleeves?\b/i,
      },
      {
        family: "long",
        structured: /\blong\b/i,
        prompt: /\blong(?:\s+\w+){0,1}\s+sleeves?\b/i,
      },
      {
        family: "three-quarter",
        structured: /\b(?:three[-\s]?quarter|3\/4)\b/i,
        prompt: /\b(?:three[-\s]?quarter|3\/4)\s+sleeves?\b/i,
      },
      {
        family: "elbow",
        structured: /\belbow\b/i,
        prompt: /\belbow(?:[-\s]length)?\s+sleeves?\b/i,
      },
      {
        family: "bell",
        structured: /\bbell\b/i,
        prompt: /\bbell(?:[-\s]?sleeved|(?:\s+\w+){0,1}\s+sleeves?)\b/i,
      },
      {
        family: "flutter",
        structured: /\bflutter\b/i,
        prompt: /\bflutter(?:[-\s]?sleeved|(?:\s+\w+){0,1}\s+sleeves?)\b/i,
      },
    ],
    neckline: [
      {
        family: "square",
        structured: /\bsquare\b/i,
        prompt: /\bsquare[-\s]?(?:neck|neckline)\b/i,
      },
      {
        family: "v-neck",
        structured: /\bv[-\s]?(?:neck|neckline)\b/i,
        prompt: /\bv[-\s]?(?:neck|neckline)\b/i,
      },
      {
        family: "plunging",
        structured: /\b(?:plunge|plunging|deep[-\s]?v)\b/i,
        prompt: /\b(?:plunge|plunging|deep[-\s]?v|deep\s+neckline)\b/i,
      },
      {
        family: "sweetheart",
        structured: /\bsweetheart\b/i,
        prompt: /\bsweetheart(?:\s+neckline)?\b/i,
      },
      {
        family: "boat",
        structured: /\bboat\b/i,
        prompt: /\bboat[-\s]?(?:neck|neckline)\b/i,
      },
      {
        family: "scoop",
        structured: /\bscoop\b/i,
        prompt: /\bscoop[-\s]?(?:neck|neckline)\b/i,
      },
      {
        family: "halter",
        structured: /\bhalter\b/i,
        prompt: /\bhalter(?:\s+neckline)?\b/i,
      },
      {
        family: "high",
        structured: /\b(?:high|mock|turtleneck)\b/i,
        prompt:
          /\b(?:high[-\s]?(?:neck|neckline|collar)|mock\s+neck|turtleneck)\b/i,
      },
      {
        family: "off-shoulder",
        structured: /\boff[-\s](?:the[-\s])?shoulder\b/i,
        prompt: /\boff[-\s](?:the[-\s])?shoulder(?:\s+neckline)?\b/i,
      },
      {
        family: "one-shoulder",
        structured: /\bone[-\s]shoulder\b/i,
        prompt: /\bone[-\s]shoulder(?:\s+neckline)?\b/i,
      },
    ],
    length: [
      {
        family: "mini",
        structured: /\b(?:mini|above[-\s]the[-\s]knee)\b/i,
        prompt: /\b(?:mini|above[-\s]the[-\s]knee)(?:[-\s]length)?\b/i,
      },
      {
        family: "knee",
        structured: /\bknee\b/i,
        prompt: /\bknee[-\s]length\b/i,
      },
      {
        family: "midi",
        structured: /\b(?:midi|tea[-\s]length|(?:mid[-\s])?calf)\b/i,
        prompt:
          /\b(?:midi|tea[-\s]length|(?:mid[-\s])?calf(?:[-\s]length)?)\b/i,
      },
      {
        family: "ankle",
        structured: /\bankle\b/i,
        prompt: /\bankle[-\s]length\b/i,
      },
      {
        family: "floor",
        structured: /\b(?:floor|maxi|cathedral|chapel|royal)\b/i,
        prompt:
          /\b(?:floor[-\s]length|maxi|cathedral(?:\s+train)?|chapel(?:\s+train)?|royal(?:\s+train)?)\b/i,
      },
    ],
    silhouette: [
      {
        family: "ballgown",
        structured: /\b(?:ball[-\s]?gown|ballgown|princess)\b/i,
        prompt: /\b(?:ball[-\s]?gown|ballgown)\b/i,
      },
      {
        family: "fitflare",
        structured: /\b(?:mermaid|trumpet|fit[\s-]?(?:and|n|&)[\s-]?flare)\b/i,
        prompt:
          /\b(?:mermaid|fit[\s-]?(?:and|n|&)[\s-]?flare|trumpet(?!\s+sleeves?))\b/i,
      },
      {
        family: "aline",
        structured: /\ba[-–\s]?line\b/i,
        prompt: /\ba[-–\s]?line(?:\s+silhouette)?\b/i,
      },
      {
        family: "sheath",
        structured: /\b(?:sheath|column)\b/i,
        prompt: /\b(?:sheath|column)(?:\s+silhouette)?\b/i,
      },
      {
        family: "empire",
        structured: /\bempire\b/i,
        prompt: /\bempire(?:[-\s]waist)?(?:\s+silhouette)?\b/i,
      },
    ],
  };

function isPositiveVisualMention(
  value: string,
  start: number,
  mention: string
): boolean {
  if (isInDesignAvoidanceScope(value, start, start + mention.length))
    return false;
  const prefix =
    value
      .slice(0, start)
      .split(/[;,!.?]|\b(?:but|then|while)\b/i)
      .at(-1) ?? "";
  return requirementViolatesAvoidance(prefix + mention, mention, {
    allowHyphenatedMatch: true,
  });
}

function visualFamilies(
  value: string | undefined,
  field: VisualScalarField,
  source: "structured" | "prompt"
): Set<string> {
  const families = new Set<string>();
  if (!value) return families;

  for (const descriptor of VISUAL_FAMILY_PATTERNS[field]) {
    const matches = [
      ...value.matchAll(new RegExp(descriptor[source].source, "gi")),
    ];
    // "Sleeveless" and "no sleeves" are both positive representations of the
    // no-sleeve family. Other negated phrases ("remove bishop sleeves", "no
    // deep neckline") describe removals/avoidances and must not be interpreted
    // as a positive visual direction.
    if (
      matches.some(
        match =>
          descriptor.family === "none" ||
          source === "structured" ||
          isPositiveVisualMention(value, match.index ?? 0, match[0])
      )
    ) {
      families.add(descriptor.family);
    }
  }
  return families;
}

interface VisualPromptMismatch {
  field: VisualScalarField | "primaryColor";
  structuredValue: string;
  promptFamilies: string[];
}

type AuthoritativeVisualScalars = Pick<
  DesignSpec["visual"],
  VisualScalarField | "primaryColor"
>;

function canonicalColorMention(value: string): string {
  return normalize(value)
    .replace(/^(emerald|sage|mint) green$/, "$1")
    .replace(/^(ruby|crimson) red$/, "$1")
    .replace(/^navy blue$/, "navy")
    .replace(/^grey$/, "gray")
    .replace(/^(wine|oxblood|merlot)$/, "burgundy");
}

function compatibleColorMentions(left: string, right: string): boolean {
  if (left === right) return true;
  // A broad color and a shade within it can both be true. Two different
  // specific shades still conflict, so an emerald-to-sage edit is reconciled.
  const baseColors: Record<string, string> = {
    emerald: "green",
    sage: "green",
    mint: "green",
    olive: "green",
    "forest green": "green",
    burgundy: "red",
    crimson: "red",
    ruby: "red",
    scarlet: "red",
    navy: "blue",
    "midnight navy": "blue",
    cobalt: "blue",
    sapphire: "blue",
    "dusty blue": "blue",
    "powder blue": "blue",
    "sky blue": "blue",
    "royal blue": "blue",
    "midnight blue": "blue",
    lavender: "purple",
    lilac: "purple",
    plum: "purple",
    violet: "purple",
    blush: "pink",
    "dusty pink": "pink",
    "dusty rose": "pink",
    charcoal: "gray",
  };
  return baseColors[left] === right || baseColors[right] === left;
}

function conflictingPrimaryColorMentions(
  prompt: string,
  primaryColor: string | undefined
): Array<{ start: number; end: number; value: string }> {
  if (!primaryColor) return [];
  const selectedColors = [...primaryColor.matchAll(DESIGN_COLOR_PATTERN)].map(
    match => canonicalColorMention(match[0])
  );
  if (selectedColors.length === 0) return [];

  return [...prompt.matchAll(DESIGN_COLOR_PATTERN)].flatMap(match => {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    // Scope accent language to the mention's own phrase. In "emerald gown
    // with gold trim", gold is an accent and emerald is the dress color.
    const boundary = /[,;.!?]|\b(?:with|and|but|featuring)\b/i;
    const before = prompt.slice(0, start).split(boundary).at(-1) ?? "";
    const after = prompt.slice(end).split(boundary)[0] ?? "";
    const context = before + match[0] + after;
    const secondaryContext =
      ACCENT_COLOR_CONTEXT.test(context) ||
      /\b(?:sleeves?|bodice|skirt|panels?|collar|cuffs?|belt|bow|hem|straps?|pockets?|colou?r[-\s]?block(?:ed|ing)?)\b/i.test(
        context
      );
    const primaryContext = PRIMARY_COLOR_CONTEXT.test(context);
    const garmentContext =
      /\b(?:dress|gown|fabric|silk|satin|chiffon|tulle|crepe|velvet|organza|taffeta)\b/i.test(
        context
      );
    if (
      selectedColors.some(color =>
        compatibleColorMentions(color, canonicalColorMention(match[0]))
      ) ||
      (!primaryContext && (secondaryContext || !garmentContext)) ||
      !isPositiveVisualMention(prompt, start, match[0])
    )
      return [];
    return [{ start, end, value: match[0] }];
  });
}

function findVisualPromptMismatches(
  prompt: string | undefined,
  visual: AuthoritativeVisualScalars
): VisualPromptMismatch[] {
  if (!prompt) return [];
  const mismatches: VisualPromptMismatch[] = [];

  for (const field of ["length", "sleeve", "neckline", "silhouette"] as const) {
    const structuredValue = compact(visual[field]);
    if (!structuredValue) continue;
    const structuredFamilies = visualFamilies(
      structuredValue,
      field,
      "structured"
    );
    const promptFamilies = visualFamilies(prompt, field, "prompt");
    if (
      structuredFamilies.size === 0 ||
      promptFamilies.size === 0 ||
      [...promptFamilies].every(family => structuredFamilies.has(family))
    ) {
      continue;
    }
    mismatches.push({
      field,
      structuredValue,
      promptFamilies: [...promptFamilies].filter(
        family => !structuredFamilies.has(family)
      ),
    });
  }
  const colorMismatches = conflictingPrimaryColorMentions(
    prompt,
    visual.primaryColor
  );
  if (colorMismatches.length > 0 && visual.primaryColor) {
    mismatches.push({
      field: "primaryColor",
      structuredValue: visual.primaryColor,
      promptFamilies: unique(colorMismatches.map(match => match.value)),
    });
  }
  return mismatches;
}

function reconcileVisualPrompt(
  prompt: string | undefined,
  visual: AuthoritativeVisualScalars
): string | undefined {
  if (!prompt || findVisualPromptMismatches(prompt, visual).length === 0) {
    return prompt;
  }

  // Remove only superseded attribute phrases, preserving fabric, movement,
  // embellishment and other choices even within the same sentence. Current
  // scalar values are rendered separately in the contract below.
  let reconciled = prompt;
  for (const match of conflictingPrimaryColorMentions(
    prompt,
    visual.primaryColor
  ).reverse()) {
    reconciled = reconciled.slice(0, match.start) + reconciled.slice(match.end);
  }
  for (const mismatch of findVisualPromptMismatches(reconciled, visual)) {
    if (mismatch.field === "primaryColor") continue;
    for (const descriptor of VISUAL_FAMILY_PATTERNS[mismatch.field]) {
      if (!mismatch.promptFamilies.includes(descriptor.family)) continue;
      const pattern = new RegExp(descriptor.prompt.source, "gi");
      reconciled = reconciled.replace(
        pattern,
        (match: string, offset: number) =>
          isPositiveVisualMention(reconciled, offset, match) ? "" : match
      );
    }
  }
  return compact(
    reconciled
      // Removing "emerald" from "an emerald gown" must not leave "an gown".
      // Keep this correction to the garment nouns with known consonant sounds.
      .replace(/\b([Aa])n\s+(?=(?:dress|gown)\b)/g, "$1 ")
      // A removed noun phrase can leave its article behind: "with a boat
      // neckline, fitted sleeves" becomes "with a, fitted sleeves".
      .replace(
        /\b(?:with|and|featuring)(?:\s+(?:a|an|the))?\s*(?=[,;.!?]|$)/gi,
        ""
      )
      .replace(/\s+([,;.!?])/g, "$1")
      .replace(/(?:,\s*){2,}/g, ", ")
      .replace(/^[\s,;]+|[\s,;]+$/g, "")
      .replace(/\s+/g, " ")
  );
}

function wasCleared(brief: DesignBrief, field: string): boolean {
  return (
    brief.clearedFields?.includes(
      field as NonNullable<DesignBrief["clearedFields"]>[number]
    ) === true
  );
}

function negativeRequirementTarget(value: string): string | undefined {
  const trimmed = compact(value);
  if (!trimmed) return undefined;
  const match = trimmed.match(
    /^(?:(?:please\s+)?(?:do\s+not|don't|dont|never|no|not|without|avoid|exclude|omit|remove)\s+)(?:(?:any|a|an|the|too)\s+)*(.+)$/i
  );
  return compact(match?.[1]);
}

function normalizeAvoidance(value: string): string {
  return negativeRequirementTarget(value) ?? value.trim();
}

function buildReferences(brief: DesignBrief): DesignSpecReference[] {
  const explicit = brief.references.roles ?? [];
  const sideSpecificUrls = new Set([
    ...brief.references.front,
    ...brief.references.back,
  ]);
  const orderedFallbacks = [
    ...brief.references.front.map(url => ({
      url,
      side: "front" as const,
      role: "overall" as DesignReferenceRole,
    })),
    ...brief.references.back.map(url => ({
      url,
      side: "back" as const,
      role: "overall" as DesignReferenceRole,
    })),
    // `inspiration` is historically a union of front/back uploads. Do not add
    // a third occurrence for the same pixel; keep it only when it is genuinely
    // side-neutral.
    ...brief.references.inspiration
      .filter(url => !sideSpecificUrls.has(url))
      .map(url => ({
        url,
        side: "inspiration" as const,
        role: "overall" as DesignReferenceRole,
      })),
  ] satisfies DesignSpecReference[];

  const usedExplicitIndexes = new Set<number>();
  const seenOccurrences = new Set<string>();
  const ordered = orderedFallbacks.flatMap(fallback => {
    const occurrenceKey = `${fallback.side}:${fallback.url}`;
    if (!fallback.url || seenOccurrences.has(occurrenceKey)) return [];
    seenOccurrences.add(occurrenceKey);

    // Enrich in place instead of putting assigned references first. Reference
    // numbers must continue to describe the upload order the image model sees,
    // even when only image 2 has been assigned a role.
    const exactIndex = explicit.findIndex(
      (candidate, index) =>
        !usedExplicitIndexes.has(index) &&
        candidate.url === fallback.url &&
        candidate.side === fallback.side
    );
    const explicitIndex =
      exactIndex >= 0
        ? exactIndex
        : explicit.findIndex(
            (candidate, index) =>
              !usedExplicitIndexes.has(index) && candidate.url === fallback.url
          );
    if (explicitIndex < 0) return [fallback];
    usedExplicitIndexes.add(explicitIndex);
    return [{ ...explicit[explicitIndex] }];
  });

  // A role written with a direct URL can predate the side lists. Preserve it
  // without disturbing the order of known uploads.
  explicit.forEach((reference, index) => {
    if (usedExplicitIndexes.has(index) || !reference.url) return;
    const occurrenceKey = `${reference.side}:${reference.url}`;
    if (seenOccurrences.has(occurrenceKey)) return;
    seenOccurrences.add(occurrenceKey);
    ordered.push({ ...reference });
  });

  return ordered;
}

function hasSemanticValue(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

function ensureProvenance(
  provenance: Record<string, DesignSpecFieldProvenance>,
  brief: DesignBrief,
  field: string,
  value: unknown,
  aliases: string[] = []
): void {
  if (!hasSemanticValue(value) || provenance[field]) return;
  const inherited = [field, ...aliases]
    .map(candidate => brief.provenance?.[candidate])
    .find(
      (candidate): candidate is DesignSpecFieldProvenance =>
        candidate !== undefined
    );
  provenance[field] = inherited ?? {
    source: "legacy",
    confirmed: false,
  };
}

/**
 * Compile the conversational brief into one deterministic contract shared by
 * confirmation UI, the image request, and the generated ticket.
 */
export function compileDesignSpec(
  brief: DesignBrief | null | undefined,
  formState: Partial<CreateFormData> = {}
): DesignSpec | undefined {
  if (!brief) return undefined;

  const primaryColor = wasCleared(brief, "primaryColor")
    ? undefined
    : (compact(brief.constraints.color.primary) ??
      compact(brief.front.color) ??
      values(formState.color)[0] ??
      brief.constraints.color.preferred[0]);
  // `preferred` is a set of alternatives considered during exploration, not a
  // palette. Only colors the customer explicitly assigned as accents belong in
  // the render contract; otherwise a comparison such as emerald vs burgundy vs
  // navy would silently turn the two unselected options into trim colors.
  const accentColors = unique(brief.constraints.color.accents ?? []).filter(
    color => !primaryColor || !sameValue(color, primaryColor)
  );
  const negativeMustHaves = brief.constraints.mustHave.flatMap(requirement => {
    const target = negativeRequirementTarget(requirement);
    return target ? [target] : [];
  });
  const mustHave = brief.constraints.mustHave.filter(
    requirement => !negativeRequirementTarget(requirement)
  );
  const modesty = wasCleared(brief, "modesty")
    ? undefined
    : (brief.constraints.modesty ?? formState.modestyPreference ?? undefined);
  const coverageFromModesty =
    modesty === "modest"
      ? "modest full-coverage design"
      : modesty === "balanced"
        ? "balanced coverage"
        : modesty === "revealing"
          ? "revealing/open coverage is permitted"
          : undefined;

  const rawPrompt = wasCleared(brief, "prompt")
    ? undefined
    : (compact(brief.front.prompt) ?? compact(formState.prompt));
  const visualWithoutPrompt = {
    primaryColor,
    accentColors,
    length: wasCleared(brief, "length")
      ? undefined
      : (compact(brief.front.length) ?? compact(formState.length)),
    sleeve: wasCleared(brief, "sleeve")
      ? undefined
      : (compact(brief.front.sleeve) ?? compact(formState.sleeve)),
    sleeveAddons: unique(brief.front.sleeveAddons),
    neckline: wasCleared(brief, "neckline")
      ? undefined
      : (compact(brief.front.neckline) ?? compact(formState.neckline)),
    waistline: wasCleared(brief, "waistline")
      ? undefined
      : (compact(brief.front.waistline) ?? compact(formState.waistline)),
    silhouette: wasCleared(brief, "silhouette")
      ? undefined
      : (compact(brief.front.silhouette) ?? compact(formState.silhouette)),
    fabric: wasCleared(brief, "fabric")
      ? undefined
      : (compact(brief.front.fabric) ?? values(formState.fabric)[0]),
    mood: unique(brief.front.mood),
    details: unique(brief.front.details),
    backPrompt: wasCleared(brief, "backPrompt")
      ? undefined
      : (compact(brief.back.prompt) ?? compact(formState.backPrompt)),
    backStyle: wasCleared(brief, "backStyle")
      ? undefined
      : compact(brief.back.style),
    closure: wasCleared(brief, "backClosure")
      ? undefined
      : compact(brief.back.closure),
  };
  if (visualWithoutPrompt.silhouette) {
    visualWithoutPrompt.silhouette = cleanDesignAttribute(
      visualWithoutPrompt.silhouette
    );
  }
  const originalLength = visualWithoutPrompt.length;
  visualWithoutPrompt.length = reconcileHighLowLength(
    visualWithoutPrompt.length,
    [...mustHave, ...(brief.constraints.construction ?? [])]
  );
  const currentPrompt =
    rawPrompt &&
    originalLength &&
    visualWithoutPrompt.length &&
    originalLength !== visualWithoutPrompt.length
      ? rawPrompt.replaceAll(originalLength, visualWithoutPrompt.length)
      : rawPrompt;
  const visual: DesignSpec["visual"] = {
    prompt: reconcileVisualPrompt(
      currentPrompt ? cleanDesignAttribute(currentPrompt) : undefined,
      visualWithoutPrompt
    ),
    ...visualWithoutPrompt,
  };
  const occasion = wasCleared(brief, "occasion")
    ? undefined
    : (compact(brief.occasion) ?? compact(formState.preset_type));
  const eventDate = wasCleared(brief, "eventDate")
    ? undefined
    : compact(brief.event?.date);
  const wearerContext = unique(brief.event?.wearerContext ?? []);
  const constraints = {
    mustHave: unique(mustHave),
    avoid: unique([
      ...brief.constraints.avoid.map(normalizeAvoidance),
      ...negativeMustHaves,
    ]).filter(requirement => !isWorkflowAvoidRequirement(requirement)),
    comfort: unique(brief.constraints.comfort),
    mobility: unique(brief.constraints.mobility),
    bodyConcerns: unique(brief.constraints.bodyConcerns ?? []),
    ...(modesty ? { modesty } : {}),
    coverage: unique([
      ...(brief.constraints.coverage ?? []),
      coverageFromModesty,
    ]),
    support: unique(brief.constraints.support ?? []),
    opacity: unique(brief.constraints.opacity ?? []),
    accessibility: unique(brief.constraints.accessibility ?? []),
    cultural: unique(brief.constraints.cultural ?? []),
    construction: unique(brief.constraints.construction ?? []),
    budget: unique(brief.constraints.budget),
  };
  const references = buildReferences(brief);
  const referenceGuidance = unique(brief.constraints.referenceGuidance ?? []);

  const provenance: Record<string, DesignSpecFieldProvenance> = {
    ...(brief.provenance ?? {}),
  };
  const clearedProvenanceFields: Partial<
    Record<NonNullable<DesignBrief["clearedFields"]>[number], string[]>
  > = {
    prompt: ["visual.prompt"],
    occasion: ["occasion", "visual.preset_type"],
    primaryColor: ["visual.primaryColor"],
    length: ["visual.length"],
    sleeve: ["visual.sleeve"],
    neckline: ["visual.neckline"],
    waistline: ["visual.waistline"],
    silhouette: ["visual.silhouette"],
    fabric: ["visual.fabric"],
    backPrompt: ["visual.backPrompt"],
    backStyle: ["visual.backStyle"],
    backClosure: ["visual.closure"],
    eventDate: ["eventDate", "event.date"],
    modesty: ["constraints.modesty"],
  };
  for (const clearedField of brief.clearedFields ?? []) {
    for (const provenanceField of clearedProvenanceFields[clearedField] ?? []) {
      delete provenance[provenanceField];
    }
  }
  ensureProvenance(provenance, brief, "occasion", occasion, [
    "visual.preset_type",
  ]);
  ensureProvenance(provenance, brief, "eventDate", eventDate, ["event.date"]);
  ensureProvenance(provenance, brief, "wearerContext", wearerContext, [
    "event.wearerContext",
  ]);
  for (const [field, value] of Object.entries(visual)) {
    ensureProvenance(provenance, brief, `visual.${field}`, value);
  }
  for (const [field, value] of Object.entries(constraints)) {
    ensureProvenance(
      provenance,
      brief,
      `constraints.${field}`,
      value,
      field === "avoid" &&
        negativeMustHaves.length > 0 &&
        brief.constraints.avoid.length === 0
        ? ["constraints.mustHave"]
        : field === "coverage" &&
            coverageFromModesty &&
            (brief.constraints.coverage ?? []).every(item =>
              sameValue(item, coverageFromModesty)
            )
          ? ["constraints.modesty"]
          : []
    );
  }
  ensureProvenance(provenance, brief, "references", references);
  ensureProvenance(provenance, brief, "referenceGuidance", referenceGuidance, [
    "constraints.referenceGuidance",
  ]);

  const downgradeMixedAggregate = (
    field: string,
    contributorFields: string[]
  ) => {
    const contributors = contributorFields.map(
      contributor => brief.provenance?.[contributor]
    );
    const known = contributors.filter(
      (candidate): candidate is DesignSpecFieldProvenance =>
        candidate !== undefined
    );
    const sameSource =
      known.length === contributors.length &&
      known.every(candidate => candidate.source === known[0]?.source);
    const confirmed =
      sameSource && known.every(candidate => candidate.confirmed);
    provenance[field] = {
      source: sameSource && known[0] ? known[0].source : "legacy",
      confirmed,
    };
  };
  if (negativeMustHaves.length > 0 && brief.constraints.avoid.length > 0) {
    downgradeMixedAggregate("constraints.avoid", [
      "constraints.avoid",
      "constraints.mustHave",
    ]);
  }
  const explicitCoverage = brief.constraints.coverage ?? [];
  if (
    coverageFromModesty &&
    explicitCoverage.some(
      requirement => !sameValue(requirement, coverageFromModesty)
    )
  ) {
    downgradeMixedAggregate("constraints.coverage", [
      "constraints.coverage",
      "constraints.modesty",
    ]);
  }

  return {
    occasion,
    eventDate,
    wearerContext,
    visual,
    constraints,
    references,
    referenceGuidance,
    provenance,
  };
}

/**
 * Deterministic compatibility projection for APIs and database columns that
 * still store the pre-DesignSpec scalar fields. When a frozen contract is
 * present, callers must use this projection instead of stale browser form
 * state. Empty strings intentionally clear legacy columns whose canonical
 * contract value was removed.
 */
export function projectDesignSpecToLegacyFormData(
  spec: DesignSpec
): Pick<
  CreateFormData,
  | "prompt"
  | "preset_type"
  | "color"
  | "length"
  | "sleeve"
  | "sleeveAddons"
  | "neckline"
  | "waistline"
  | "silhouette"
  | "fabric"
  | "backPrompt"
> {
  const frontPromptCandidate =
    compact(spec.visual.prompt) ??
    (unique([
      spec.occasion ? `${spec.occasion} dress` : undefined,
      spec.visual.primaryColor,
      spec.visual.length,
      spec.visual.sleeve,
      spec.visual.neckline,
      spec.visual.waistline,
      spec.visual.silhouette,
      spec.visual.fabric,
      ...spec.visual.details,
      ...spec.constraints.mustHave,
    ]).join(", ") ||
      "Custom dress defined by the design contract");
  const derivedFrontPrompt =
    frontPromptCandidate.length >= 10
      ? frontPromptCandidate
      : `${frontPromptCandidate} custom dress`;
  const backPromptCandidate =
    compact(spec.visual.backPrompt) ??
    unique([spec.visual.backStyle, spec.visual.closure]).join(", ");
  const derivedBackPrompt =
    backPromptCandidate.length === 0 || backPromptCandidate.length >= 10
      ? backPromptCandidate
      : `${backPromptCandidate} back design`;

  return {
    prompt: derivedFrontPrompt,
    preset_type: spec.occasion ?? "",
    color: spec.visual.primaryColor ?? "",
    length: spec.visual.length ?? "",
    sleeve: spec.visual.sleeve ?? "",
    sleeveAddons: [...spec.visual.sleeveAddons],
    neckline: spec.visual.neckline ?? "",
    waistline: spec.visual.waistline ?? "",
    silhouette: spec.visual.silhouette ?? "",
    fabric: spec.visual.fabric ?? "",
    backPrompt: derivedBackPrompt,
  };
}

export interface DesignSpecValidation {
  valid: boolean;
  conflicts: string[];
  warnings: string[];
}

const CONSTRAINT_GRAMMAR_WORDS = new Set([
  "a",
  "an",
  "and",
  "avoid",
  "do",
  "dont",
  "have",
  "keep",
  "must",
  "no",
  "not",
  "the",
  "without",
]);

function canonicalConstraintWord(word: string): string {
  const aliases: Record<string, string> = {
    beadwork: "bead",
    beaded: "bead",
    beading: "bead",
    embroidered: "embroider",
    embroidery: "embroider",
    plunged: "plunge",
    plunging: "plunge",
    sequined: "sequin",
    sparkling: "sparkle",
  };
  if (aliases[word]) return aliases[word];
  if (word.length > 4 && word.endsWith("ies")) {
    return `${word.slice(0, -3)}y`;
  }
  if (word.length > 4 && word.endsWith("s") && !word.endsWith("ss")) {
    return word.slice(0, -1);
  }
  return word;
}

function constraintWords(value: string): string[] {
  return normalize(value)
    .split(/\s+/)
    .filter(Boolean)
    .filter(word => !CONSTRAINT_GRAMMAR_WORDS.has(word))
    .map(canonicalConstraintWord);
}

function requirementViolatesAvoidance(
  requirement: string,
  avoidance: string,
  options: { allowHyphenatedMatch?: boolean } = {}
): boolean {
  const avoidanceWords = constraintWords(avoidance);
  if (avoidanceWords.length === 0) return false;

  // Repeating an excluded feature in a new "with" phrase reintroduces it:
  // "without lace with lace sleeves" still requires lace. Other "with"
  // phrases can qualify the exclusion itself ("avoid dresses with lace"),
  // so preserve those complete scopes for the shared exclusion parser.
  const scopedRequirement = requirement.replace(/\bwith\b/gi, (word, start) => {
    const prefix =
      requirement
        .slice(0, start)
        .split(/[;,.!?]|\b(?:but|then|while|with)\b/i)
        .at(-1) ?? "";
    const suffix = requirement.slice(start + word.length);
    if (
      /\bwithout\b/i.test(prefix) &&
      avoidanceWords.some(
        target =>
          constraintWords(prefix).includes(target) &&
          constraintWords(suffix).includes(target)
      )
    ) {
      return `; ${word}`;
    }
    return word;
  });
  return stripAvoidRequirementClauses(scopedRequirement)
    .split(
      /[;,.!?]|\b(?:but|then|while)\b|\band\s+(?=(?:keep|use|add|include|make|with|a|an|the|some)\b)/i
    )
    .some(clause => {
      const tokens = Array.from(clause.toLowerCase().matchAll(/[a-z0-9]+/g));
      const positiveWords = new Set<string>();
      let negated = false;
      for (const [index, token] of tokens.entries()) {
        const word = canonicalConstraintWord(token[0]);
        if (word === "with" || word === "and") negated = false;
        if (
          /^(?:no|not|non|never|without|avoid|exclude|omit|remove|dislike|hate)$/.test(
            word
          ) &&
          // "Not only lace" still positively asks for lace.
          !(word === "not" && tokens[index + 1]?.[0] === "only")
        ) {
          negated = true;
        }
        if (negated) continue;
        // A short shared modifier can cross "and" ("not low and revealing"),
        // but a completed predicate must not negate a new feature further on
        // ("neckline that is not very low and long sleeves").
        const prefix = tokens.slice(Math.max(0, index - 3), index);
        if (
          prefix.some(token => token[0] === "and") &&
          prefix.some(
            (token, prefixIndex) =>
              /^(?:no|not|non|never|without)$/.test(token[0]) &&
              !(token[0] === "not" && prefix[prefixIndex + 1]?.[0] === "only")
          )
        ) {
          continue;
        }
        const nextWord = tokens[index + 1]?.[0];
        if (nextWord === "free" || nextWord === "less") continue;

        // Check each surviving occurrence, not the whole requirement: a
        // negated standalone "lace" must not reactivate a later "lace-up".
        const start = token.index ?? 0;
        const isHyphenated =
          clause[start - 1] === "-" || clause[start + token[0].length] === "-";
        if (
          options.allowHyphenatedMatch !== true &&
          avoidanceWords.length === 1 &&
          isHyphenated
        ) {
          continue;
        }
        positiveWords.add(word);
      }
      // Match only positive occurrences of every required word. This keeps
      // "neckline that is not very low" compatible with Avoid "very low
      // neckline", but still conflicting with the broader Avoid "neckline".
      return avoidanceWords.every(word => positiveWords.has(word));
    });
}

export function getDesignSpecReferencesForSide(
  spec: DesignSpec,
  side: "front" | "back"
): DesignSpecReference[] {
  return spec.references.filter(reference => {
    if (reference.role === "back" && side === "front") return false;
    return reference.side === side || reference.side === "inspiration";
  });
}

export function validateDesignSpec(
  spec: DesignSpec,
  options: {
    side?: "front" | "back";
    /**
     * Current-turn edit instructions that will be sent to the renderer in
     * addition to the frozen contract. This closes the modification path where
     * an edit could reintroduce something the contract explicitly forbids.
     */
    additionalPositiveRequirements?: Array<
      string | { label: string; value: string }
    >;
  } = {}
): DesignSpecValidation {
  const conflicts: string[] = [];
  const warnings: string[] = [];

  for (const mismatch of findVisualPromptMismatches(spec.visual.prompt, {
    primaryColor: spec.visual.primaryColor,
    length: spec.visual.length,
    sleeve: spec.visual.sleeve,
    neckline: spec.visual.neckline,
    silhouette: spec.visual.silhouette,
  })) {
    conflicts.push(
      `Visual front prompt names ${mismatch.promptFamilies.join(
        "/"
      )} ${mismatch.field}, but the authoritative ${mismatch.field} is "${mismatch.structuredValue}".`
    );
  }

  for (const requirement of spec.constraints.mustHave) {
    if (
      spec.constraints.avoid.some(avoidance =>
        requirementViolatesAvoidance(requirement, avoidance)
      )
    ) {
      conflicts.push(`"${requirement}" appears in both Must have and Avoid.`);
    }
  }

  const functionalRequirements: Array<[string, string]> = [
    ...spec.constraints.comfort.map((value): [string, string] => [
      "comfort requirement",
      value,
    ]),
    ...spec.constraints.mobility.map((value): [string, string] => [
      "movement requirement",
      value,
    ]),
    ...spec.constraints.bodyConcerns.map((value): [string, string] => [
      "body/fit requirement",
      value,
    ]),
    ...spec.constraints.coverage.map((value): [string, string] => [
      "coverage requirement",
      value,
    ]),
    ...spec.constraints.support.map((value): [string, string] => [
      "support requirement",
      value,
    ]),
    ...spec.constraints.opacity.map((value): [string, string] => [
      "opacity/lining requirement",
      value,
    ]),
    ...spec.constraints.accessibility.map((value): [string, string] => [
      "accessibility requirement",
      value,
    ]),
    ...spec.constraints.cultural.map((value): [string, string] => [
      "cultural requirement",
      value,
    ]),
    ...spec.constraints.construction.map((value): [string, string] => [
      "construction requirement",
      value,
    ]),
    ...(spec.constraints.modesty
      ? [
          [
            "modesty preference",
            spec.constraints.modesty === "modest"
              ? "modest full coverage"
              : spec.constraints.modesty === "balanced"
                ? "balanced coverage"
                : "revealing open coverage",
          ] as [string, string],
        ]
      : []),
    ...(options.additionalPositiveRequirements ?? []).map(
      (requirement): [string, string] =>
        typeof requirement === "string"
          ? ["modification request", requirement]
          : [requirement.label, requirement.value]
    ),
  ];
  for (const [field, requirement] of functionalRequirements) {
    for (const avoidance of spec.constraints.avoid) {
      if (!requirementViolatesAvoidance(requirement, avoidance)) continue;
      conflicts.push(
        `${field[0].toUpperCase()}${field.slice(
          1
        )} "${requirement}" conflicts with Avoid "${avoidance}".`
      );
    }
  }

  const visualRequirements: Array<[string, string | undefined]> = [
    ["front prompt", spec.visual.prompt],
    ["primary color", spec.visual.primaryColor],
    ...spec.visual.accentColors.map((value): [string, string] => [
      "accent color",
      value,
    ]),
    ["length", spec.visual.length],
    ["sleeve", spec.visual.sleeve],
    ...spec.visual.sleeveAddons.map((value): [string, string] => [
      "sleeve addition",
      value,
    ]),
    ["neckline", spec.visual.neckline],
    ["waistline", spec.visual.waistline],
    ["silhouette", spec.visual.silhouette],
    ["fabric", spec.visual.fabric],
    ...spec.visual.mood.map((value): [string, string] => ["mood", value]),
    ...spec.visual.details.map((value): [string, string] => [
      "visual detail",
      value,
    ]),
    ...(options.side === "front"
      ? []
      : ([
          ["back prompt", spec.visual.backPrompt],
          ["back style", spec.visual.backStyle],
          ["closure", spec.visual.closure],
        ] as Array<[string, string | undefined]>)),
  ];
  for (const [field, value] of visualRequirements) {
    if (!value) continue;
    for (const avoidance of spec.constraints.avoid) {
      if (!requirementViolatesAvoidance(value, avoidance)) continue;
      conflicts.push(
        `Visual ${field} "${value}" conflicts with Avoid "${avoidance}".`
      );
    }
  }

  if (!spec.visual.prompt && !spec.visual.silhouette) {
    warnings.push("The visual direction is still sparse.");
  }
  if (!spec.visual.primaryColor) {
    warnings.push("No primary color is confirmed.");
  }
  const sidesToCheck = options.side ? [options.side] : ["front", "back"];
  for (const side of sidesToCheck) {
    const unscoped = getDesignSpecReferencesForSide(
      spec,
      side as "front" | "back"
    ).filter(
      reference =>
        reference.role === "overall" && !compact(reference.instruction)
    );
    if (unscoped.length > 1) {
      conflicts.push(
        `${side === "front" ? "Front" : "Back"} generation has ${unscoped.length} references without distinct roles. Assign what each reference controls before continuing.`
      );
    }
  }

  return { valid: conflicts.length === 0, conflicts, warnings };
}

export interface ExtractedDesignSpecAttributes {
  sleeve?: string;
  length?: string;
  neckline?: string;
  silhouette?: string;
  fabric?: string;
  color?: string;
}

/**
 * Finalize an upload render contract with reference-derived attributes.
 * Explicit/chat-authored values always win. Each missing field is accepted
 * only when the resulting contract remains schema-valid and does not conflict
 * with the customer's prompt or Avoid constraints.
 */
export function mergeExtractedAttributesIntoDesignSpec(
  designSpec: DesignSpec,
  extracted: ExtractedDesignSpecAttributes | null | undefined,
  options: { side?: "front" | "back" } = {}
): DesignSpec {
  let finalized = designSpecSchema.parse(designSpec);
  if (!extracted) return finalized;

  const candidates: Array<{
    field:
      | "primaryColor"
      | "length"
      | "sleeve"
      | "neckline"
      | "silhouette"
      | "fabric";
    value: string | undefined;
    provenance: string;
  }> = [
    {
      field: "primaryColor",
      value: extracted.color,
      provenance: "visual.primaryColor",
    },
    { field: "length", value: extracted.length, provenance: "visual.length" },
    { field: "sleeve", value: extracted.sleeve, provenance: "visual.sleeve" },
    {
      field: "neckline",
      value: extracted.neckline,
      provenance: "visual.neckline",
    },
    {
      field: "silhouette",
      value: extracted.silhouette,
      provenance: "visual.silhouette",
    },
    { field: "fabric", value: extracted.fabric, provenance: "visual.fabric" },
  ];

  for (const candidate of candidates) {
    const value = compact(candidate.value);
    if (!value || compact(finalized.visual[candidate.field])) continue;

    const next = designSpecSchema.parse({
      ...finalized,
      visual: {
        ...finalized.visual,
        [candidate.field]: value,
      },
      provenance: {
        ...finalized.provenance,
        [candidate.provenance]: {
          source: "reference",
          confirmed: false,
        },
      },
    });
    if (validateDesignSpec(next, options).valid) finalized = next;
  }

  return finalized;
}

/** Only attributes that actually filled an empty contract field are safe to
 * project back into the editable form. Raw reference analysis must never
 * overwrite an explicit customer-authored value. */
export function getAppliedExtractedDesignSpecAttributes(
  before: DesignSpec,
  after: DesignSpec
): ExtractedDesignSpecAttributes {
  const applied: ExtractedDesignSpecAttributes = {};
  const mappings = [
    ["color", "primaryColor"],
    ["length", "length"],
    ["sleeve", "sleeve"],
    ["neckline", "neckline"],
    ["silhouette", "silhouette"],
    ["fabric", "fabric"],
  ] as const;

  for (const [attribute, field] of mappings) {
    if (compact(before.visual[field])) continue;
    const value = compact(after.visual[field]);
    if (value) applied[attribute] = value;
  }
  return applied;
}

function line(label: string, entries: string[]): string | null {
  return entries.length > 0 ? `- ${label}: ${entries.join("; ")}` : null;
}

/**
 * Render-facing constraint block. It contains only concrete design guidance;
 * budget language is translated into construction choices instead of asking
 * the image model to visualize a price.
 */
export function buildDesignSpecPromptSection(
  spec: DesignSpec | null | undefined,
  options: { side?: "front" | "back" } = {}
): string {
  if (!spec) return "";
  // A shared requirement may repeat a back detail even when visual.backPrompt
  // is correctly scoped. Project only the front-facing positive text here;
  // retain the complete saved contract for later back generation.
  const positiveText = (value: string) =>
    options.side === "front" ? cleanFrontPromptCandidate(value) : value;
  const positiveEntries = (entries: string[]) =>
    entries.map(positiveText).filter(Boolean);
  const overallPrompt = positiveText(spec.visual.prompt ?? "");
  const references = options.side
    ? getDesignSpecReferencesForSide(spec, options.side)
    : spec.references;
  const referenceInstructions = [
    ...spec.referenceGuidance,
    ...references.map(
      (reference, index) =>
        `Reference ${index + 1} (${reference.side}): use for ${reference.role}${
          reference.instruction ? `; ${reference.instruction}` : ""
        }`
    ),
  ];
  const lines = [
    spec.occasion ? `- Occasion: ${spec.occasion}` : null,
    spec.eventDate ? `- Event date: ${spec.eventDate}` : null,
    line("Wearer context", spec.wearerContext),
    overallPrompt ? `- Overall front design: ${overallPrompt}` : null,
    spec.visual.primaryColor
      ? `- Primary dress color: ${spec.visual.primaryColor}`
      : null,
    line("Accent colors only", spec.visual.accentColors),
    spec.visual.length ? `- Dress length: ${spec.visual.length}` : null,
    spec.visual.sleeve ? `- Sleeve style: ${spec.visual.sleeve}` : null,
    line("Sleeve additions", spec.visual.sleeveAddons),
    spec.visual.neckline ? `- Neckline: ${spec.visual.neckline}` : null,
    spec.visual.waistline ? `- Waistline: ${spec.visual.waistline}` : null,
    spec.visual.silhouette ? `- Silhouette: ${spec.visual.silhouette}` : null,
    spec.visual.fabric ? `- Fabric: ${spec.visual.fabric}` : null,
    line("Mood", spec.visual.mood),
    line("Visual details", positiveEntries(spec.visual.details)),
    options.side !== "front" && spec.visual.backPrompt
      ? `- Back design: ${spec.visual.backPrompt}`
      : null,
    options.side !== "front" && spec.visual.backStyle
      ? `- Back style: ${spec.visual.backStyle}`
      : null,
    options.side !== "front" && spec.visual.closure
      ? `- Back closure: ${spec.visual.closure}`
      : null,
    line("Must preserve", positiveEntries(spec.constraints.mustHave)),
    line("Must not include", spec.constraints.avoid),
    line("Comfort and wearability", spec.constraints.comfort),
    line("Movement requirements", spec.constraints.mobility),
    spec.constraints.modesty
      ? `- Modesty preference: ${spec.constraints.modesty}`
      : null,
    line("Coverage requirements", spec.constraints.coverage),
    line("Support requirements", spec.constraints.support),
    line("Opacity and lining", spec.constraints.opacity),
    line("Accessibility requirements", spec.constraints.accessibility),
    line("Cultural requirements", spec.constraints.cultural),
    line("Construction requirements", spec.constraints.construction),
    line("Body and fit sensitivities", spec.constraints.bodyConcerns),
    line("Reference roles", referenceInstructions),
    spec.constraints.budget.length > 0
      ? "- Construction economy: favor elegant lower-complexity construction and material-efficient detailing unless a must-have explicitly requires otherwise"
      : null,
  ].filter((item): item is string => Boolean(item));

  return lines.length > 0 ? `Design Contract:\n${lines.join("\n")}` : "";
}
